import { useEffect, useState } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { zeroPad } from "../../../app/utils";

interface Props {
	msToRun: number;
	isRunning: boolean;
	reset?: boolean;
	onTick?: (ms: number) => void;
	onTimeout?: () => void;
}

export default function WritingTimer(props: Props) {
	const [startTime, setStartTime] = useState(new Date().getTime());
	const [remainingMs, setRemainingMs] = useState(props.msToRun);
	const [countdown, setCountdown] = useState("--:--");
	const [isCountdownHidden, setIsCountdownHidden] = useState(false);

	useEffect(() => {
		if (props.reset === true) {
			setRemainingMs(props.msToRun);
		}
	}, [props.reset]);

	useEffect(() => {
		if (props.isRunning) {
			setStartTime(new Date().getTime());
		}
	}, [props.isRunning]);

	useEffect(() => {
		if (props.isRunning) {
			setStartTime(new Date().getTime());
		}
		setRemainingMs(props.msToRun);
	}, [props.msToRun]);

	useEffect(() => {
		setCountdown(formatTime(remainingMs / 1000));
	}, [remainingMs]);

	useEffect(() => {
		if (remainingMs === 0 || !props.isRunning) return;

		const timer = setTimeout(() => {
			const elapsedMs = new Date().getTime() - startTime;
			const remainingMs = Math.max(props.msToRun - elapsedMs, 0);
			setRemainingMs(remainingMs);
			props.onTick && props.onTick(remainingMs);
			if (remainingMs === 0) {
				props.onTimeout && props.onTimeout();
			}
		}, 250);

		return () => clearTimeout(timer);
	});

	const formatTime = (seconds: number) => {
		const secs = seconds !== 0 ? seconds + 0.99 : 0;
		const h = Math.floor(secs / 3600).toString();
		const mm = zeroPad(Math.floor((secs % 3600) / 60), 2);
		const ss = zeroPad(Math.floor(secs % 60), 2);

		return secs > 3600 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
	};

	const toggleTime = () => {
		setIsCountdownHidden(!isCountdownHidden);
	};

	return (
		<div id="writing-timer" className="w-64 cursor-default">
			<div className="flex justify-between bg-red-600 text-white p-2">
				<div>
					<ClockIcon className="h-5 w-5 inline" /> Time Remaining
				</div>
				<div onClick={toggleTime} className="cursor-pointer">
					{isCountdownHidden ? (
						<span className="text-sm">
							Show <ChevronDownIcon className="h-4 w-4 inline" />
						</span>
					) : (
						<span className="text-sm">
							Hide <ChevronUpIcon className="h-4 w-4 inline" />
						</span>
					)}
				</div>
			</div>
			<div
				id="timer-countdown"
				className={`${
					isCountdownHidden ? "opacity-0" : "opacity-100"
				} transition-opacity ease-in-out delay-150 border-2 border-gray-400 flex items-center justify-center p-4 text-2xl font-bold `}>
				{countdown}
			</div>
		</div>
	);
}

export function TimerTest() {
	const defaultMsRemaining = 4 * 1000;

	const [isRunning, setIsRunning] = useState(false);
	const [msRemaining, setMsRemaining] = useState(defaultMsRemaining);
	const [initialMsRemaining, setInitialMsRemaining] = useState(defaultMsRemaining);
	const [reset, setReset] = useState<boolean>();

	function handleStop() {
		console.log("handleStop");
		setIsRunning(false);
	}
	function handleStart() {
		console.log("handleStart");
		setReset(false);
		setIsRunning(true);
		setInitialMsRemaining(msRemaining);
	}
	function handleTimeout() {
		console.log("handleTimeout");
		setInitialMsRemaining(0);
		setMsRemaining(0);
		setTimeout(() => alert("timeout"), 10);
	}
	function handleTick(ms: number) {
		console.log(`handleTick: ms=${ms}`);
		setMsRemaining(ms);
	}
	function handleReset() {
		console.log("handleReset");
		setIsRunning(false);
		setInitialMsRemaining(defaultMsRemaining);
		setMsRemaining(defaultMsRemaining);
		setReset(true);
	}
	return (
		<>
			<WritingTimer
				msToRun={initialMsRemaining}
				isRunning={isRunning}
				reset={reset}
				onTick={handleTick}
				onTimeout={handleTimeout}
			/>

			{isRunning ? (
				<button className="btn btn-primary mt-4" onClick={handleStop}>
					stop
				</button>
			) : (
				<button className="btn btn-primary mt-4" onClick={handleStart}>
					start
				</button>
			)}
			<button className="btn btn-primary mt-4 ml-4" onClick={handleReset}>
				reset
			</button>

			<div className="pt-8">Seconds remaining: {msRemaining / 1000}</div>

			{msRemaining === 0 && <div>Time’s up!</div>}
		</>
	);
}
